import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'

interface Organization {
  id: string
  name: string
  businessId?: string
  type: string
  address?: string
  postalCode?: string
  city?: string
  phone?: string
  email?: string
  website?: string
  description?: string
  logoUrl?: string
  vatRegistered: boolean
  fiscalYearStart: string
}

interface FormData {
  name: string
  businessId: string
  type: string
  address: string
  postalCode: string
  city: string
  phone: string
  email: string
  website: string
  description: string
  fiscalYearStart: string
  vatRegistered: boolean
}

export default function OrganizationSettingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeOrg: organization, setActiveOrg: setOrganization, deleteOrganization } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [orgData, setOrgData] = useState<Organization | null>(null)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    businessId: '',
    type: 'association',
    address: '',
    postalCode: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    fiscalYearStart: '01-01',
    vatRegistered: false,
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch organization details
  useEffect(() => {
    if (organization?.id) {
      fetchOrganization()
    }
  }, [organization?.id])

  const fetchOrganization = async () => {
    if (!organization?.id) return
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await apiClient.get<{ organization: Organization }>(`/organizations/${organization.id}`)
      const org = response.organization
      setOrgData(org)
      setFormData({
        name: org.name || '',
        businessId: org.businessId || '',
        type: org.type || 'association',
        address: org.address || '',
        postalCode: org.postalCode || '',
        city: org.city || '',
        phone: org.phone || '',
        email: org.email || '',
        website: org.website || '',
        description: org.description || '',
        fiscalYearStart: org.fiscalYearStart || '01-01',
        vatRegistered: org.vatRegistered || false,
      })
      if (org.logoUrl) {
        setLogoPreview(org.logoUrl)
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('org.failedToGet'))
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      setError(t('org.invalidLogoType'))
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(t('org.logoTooLarge'))
      return
    }

    setLogoFile(file)
    setLogoPreview(URL.createObjectURL(file))
    setError(null)
  }

  const handleLogoUpload = async () => {
    if (!logoFile || !organization?.id) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const formData = new FormData()
      formData.append('logo', logoFile)

      const response = await apiClient.post<{ success: boolean; logoUrl: string }>(`/organizations/${organization.id}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.success) {
        setSuccess(t('org.logoUploadSuccess'))
        setOrgData(prev => prev ? { ...prev, logoUrl: response.logoUrl } : null)
        setLogoFile(null)
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('org.logoUploadFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleLogoDelete = async () => {
    if (!organization?.id || !orgData?.logoUrl) return

    if (!window.confirm(t('org.confirmLogoDelete'))) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await apiClient.delete<{ success: boolean }>(`/organizations/${organization.id}/logo`)

      if (response.success) {
        setSuccess(t('org.logoDeleteSuccess'))
        setOrgData(prev => prev ? { ...prev, logoUrl: undefined } : null)
        setLogoPreview(null)
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('org.logoDeleteFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteOrganization = async () => {
    if (!organization?.id) return
    const confirmed = window.confirm(
      t('org.confirmDelete', { name: orgData?.name || organization.name })
    )
    if (!confirmed) return

    setDeleting(true)
    setError(null)
    try {
      await deleteOrganization(organization.id)
      navigate('/')
    } catch (err: any) {
      setError(err.message || t('org.deleteFailed'))
      setDeleting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organization?.id) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await apiClient.put<{ success: boolean }>(`/organizations/${organization.id}`, {
        name: formData.name,
        businessId: formData.businessId || undefined,
        type: formData.type,
        address: formData.address || undefined,
        postalCode: formData.postalCode || undefined,
        city: formData.city || undefined,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        website: formData.website || undefined,
        description: formData.description || undefined,
        fiscalYearStart: formData.fiscalYearStart,
        vatRegistered: formData.vatRegistered,
      })

      if (response.success) {
        setSuccess(t('org.saveSuccess'))
        // Update organization in context if name changed
        if (organization && formData.name !== organization.name) {
          setOrganization({ ...organization, name: formData.name })
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('org.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        {t('org.settingsTitle')}
      </h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Logo Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t('org.logoTitle')}
        </h2>
        
        <div className="flex items-center space-x-6">
          <div className="flex-shrink-0">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Organization logo"
                className="h-24 w-24 object-contain rounded-lg border border-gray-200 dark:border-gray-700"
              />
            ) : (
              <div className="h-24 w-24 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <span className="text-gray-400 text-3xl">🏢</span>
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
              onChange={handleLogoChange}
              className="hidden"
            />
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {logoPreview ? t('org.changeLogo') : t('org.uploadLogo')}
              </button>
              
              {logoFile && (
                <button
                  type="button"
                  onClick={handleLogoUpload}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? t('common.saving') : t('org.saveLogo')}
                </button>
              )}
              
              {orgData?.logoUrl && (
                <button
                  type="button"
                  onClick={handleLogoDelete}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-red-300 dark:border-red-600 rounded-md shadow-sm text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {t('org.deleteLogo')}
                </button>
              )}
            </div>
            
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('org.logoHelp')}
            </p>
          </div>
        </div>
      </div>

      {/* Organization Details Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t('org.detailsTitle')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div className="col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Business ID */}
          <div>
            <label htmlFor="businessId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.businessId')}
            </label>
            <input
              type="text"
              id="businessId"
              name="businessId"
              value={formData.businessId}
              onChange={handleInputChange}
              placeholder="1234567-8"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Type */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.type')}
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="association">{t('org.typeAssociation')}</option>
              <option value="company">{t('org.typeCompany')}</option>
              <option value="foundation">{t('org.typeFoundation')}</option>
              <option value="other">{t('org.typeOther')}</option>
            </select>
          </div>

          {/* Address */}
          <div className="col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.address')}
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Postal Code */}
          <div>
            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.postalCode')}
            </label>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.city')}
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.phone')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Website */}
          <div className="col-span-2">
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.website')}
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Fiscal Year Start */}
          <div>
            <label htmlFor="fiscalYearStart" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.fiscalYearStart')}
            </label>
            <input
              type="text"
              id="fiscalYearStart"
              name="fiscalYearStart"
              value={formData.fiscalYearStart}
              onChange={handleInputChange}
              placeholder="MM-DD"
              pattern="\\d{2}-\\d{2}"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">{t('org.fiscalYearHelp')}</p>
          </div>

          {/* VAT Registered */}
          <div className="flex items-center h-full pt-6">
            <input
              type="checkbox"
              id="vatRegistered"
              name="vatRegistered"
              checked={formData.vatRegistered}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="vatRegistered" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              {t('org.vatRegistered')}
            </label>
          </div>

          {/* Description */}
          <div className="col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('org.description')}
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('org.saveChanges')}
          </button>
        </div>
      </form>
      {/* Danger Zone */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-red-200 dark:border-red-800">
        <h2 className="text-lg font-medium text-red-700 dark:text-red-400 mb-2">
          {t('org.dangerZone')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('org.deleteWarning')}
        </p>
        <button
          type="button"
          onClick={handleDeleteOrganization}
          disabled={deleting || saving}
          className="inline-flex items-center px-4 py-2 border border-red-300 dark:border-red-600 rounded-md shadow-sm text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
        >
          {deleting ? t('common.deleting') : t('org.deleteOrganization')}
        </button>
      </div>
    </div>
  )
}
